import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import {
  applyTheme,
  type ResolvedTheme,
  readStoredPreference,
  resolveTheme,
  type ThemePreference,
  themeStorageKey,
} from './theme-script.js';

/** Called when the preference changes, so an application can persist it per user on the server. */
export type PersistThemePreference = (preference: ThemePreference) => void | Promise<void>;

export interface UseThemeOptions {
  /** Server persistence. `localStorage` is always written, for the pre-login and pre-paint paths. */
  readonly persist?: PersistThemePreference;
  /** The preference the server already knows, applied once when it arrives after login. */
  readonly serverPreference?: ThemePreference;
}

export interface ThemeState {
  readonly preference: ThemePreference;
  readonly resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
  /** Cycles light, dark, system, which is what a single toolbar button does. */
  cycle: () => void;
}

const darkQuery = '(prefers-color-scheme: dark)';

function subscribeToSystem(onChange: () => void): () => void {
  const media = globalThis.matchMedia?.(darkQuery);
  media?.addEventListener('change', onChange);
  return () => media?.removeEventListener('change', onChange);
}

function systemIsDark(): boolean {
  return globalThis.matchMedia?.(darkQuery).matches === true;
}

/**
 * The theme preference and the theme actually applied.
 *
 * System mode follows the operating system live, because a person who switches their machine to
 * dark at sunset expects the application to follow without a reload.
 */
export function useTheme(options: UseThemeOptions = {}): ThemeState {
  const { persist, serverPreference } = options;
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);

  const prefersDark = useSyncExternalStore(subscribeToSystem, systemIsDark, () => false);

  // The server's preference wins once it is known: it is the account setting, and localStorage is
  // only a mirror for the paths that run before the account is loaded.
  // Depending on `preference` too would let a stale server value undo a local choice.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs only when the server answer changes
  useEffect(() => {
    if (serverPreference !== undefined && serverPreference !== preference) {
      setPreferenceState(serverPreference);
    }
  }, [serverPreference]);

  useEffect(() => {
    applyTheme(preference);
    try {
      globalThis.localStorage?.setItem(themeStorageKey, preference);
    } catch {
      // Storage may be unavailable; the theme still applies for this session.
    }
  }, [preference]);

  const setPreference = useCallback(
    (next: ThemePreference) => {
      setPreferenceState(next);
      void persist?.(next);
    },
    [persist],
  );

  const cycle = useCallback(() => {
    setPreference(preference === 'system' ? 'light' : preference === 'light' ? 'dark' : 'system');
  }, [preference, setPreference]);

  const resolved = useMemo<ResolvedTheme>(
    () => (preference === 'system' ? (prefersDark ? 'dark' : 'light') : resolveTheme(preference)),
    [preference, prefersDark],
  );

  return { preference, resolved, setPreference, cycle };
}
